import type {
  KnownNumberEntry,
  KnownNumberPart,
  EncodingSystem,
  SavedAssociation,
} from "./types";
import { DEFAULT_ENCODING_SYSTEM } from "./encodingProfiles";

export type KnownNumberSubgroup = {
  name: string;
  entries: KnownNumberEntry[];
};

export type KnownNumberGroup = {
  name: string;
  subgroups: KnownNumberSubgroup[];
};

export type TextareaPointerMetrics = {
  pointerY: number;
  paddingTop: number;
  lineHeight: number;
  scrollTop: number;
};

export type KnownNumberPointerMatch = {
  entry: KnownNumberEntry;
  part: KnownNumberPart;
};

const STARTUP_NUMBER_TARGETS = [
  { digits: "3141592653589793", terms: ["math.pi", "archimedes"] },
  { digits: "299792458", terms: ["speed of light"] },
  { digits: "1618033988749895", terms: ["golden ratio"] },
  { digits: "1643", terms: ["isaac newton"] },
  { digits: "1915", terms: ["general relativity", "relativity"] },
  { digits: "331", terms: ["gaugamela"] },
  { digits: "9223372036854775807", terms: ["long.max_value"] },
];

export function filterKnownNumbers(
  entries: KnownNumberEntry[],
  query: string,
): KnownNumberEntry[] {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) {
    return entries;
  }
  return entries.filter((entry) =>
    [entry.group, entry.subgroup, entry.label, ...entry.numbers]
      .join(" ")
      .toLocaleLowerCase()
      .includes(normalized),
  );
}

export function groupKnownNumbers(entries: KnownNumberEntry[]): KnownNumberGroup[] {
  const groups: KnownNumberGroup[] = [];
  for (const entry of entries) {
    let group = groups.find((candidate) => candidate.name === entry.group);
    if (!group) {
      group = { name: entry.group, subgroups: [] };
      groups.push(group);
    }
    let subgroup = group.subgroups.find(
      (candidate) => candidate.name === entry.subgroup,
    );
    if (!subgroup) {
      subgroup = { name: entry.subgroup, entries: [] };
      group.subgroups.push(subgroup);
    }
    subgroup.entries.push(entry);
  }
  return groups;
}

export function savedAssociationsAsKnownNumbers(
  associations: SavedAssociation[],
  encodingSystem: EncodingSystem = DEFAULT_ENCODING_SYSTEM,
  labels: {
    group: string;
    mapping: string;
    subgroup: string;
  } = {
    group: "Saved numbers",
    mapping: "saved mapping",
    subgroup: "My associations",
  },
): KnownNumberEntry[] {
  return associations
    .filter(
      (association) =>
        (association.encoding_system ?? "english") === encodingSystem,
    )
    .map((association) => {
    const words = association.candidates.map((candidate) => candidate.noun).join(" ");
    return {
      id: `saved-association:${association.id}`,
      group: labels.group,
      subgroup: labels.subgroup,
      label: `${association.digits} - ${words}`,
      numbers: [association.digits],
      evidence_url: "",
      representation: `${association.digits} = ${words}`,
      parts: [
        {
          digits: association.digits,
          display: association.digits,
          meaning: `${labels.mapping}: ${words}`,
        },
      ],
      saved_association: association,
    };
    });
}

export function flattenKnownNumberEntries(
  entries: KnownNumberEntry[],
): string[] {
  return entries.flatMap((entry) => entry.numbers);
}

export function startupKnownNumberSelection(
  entries: KnownNumberEntry[],
  maximumRows: number,
): KnownNumberEntry[] {
  const selected: KnownNumberEntry[] = [];
  for (const target of STARTUP_NUMBER_TARGETS) {
    const candidates = entries
      .filter((entry) => {
        const searchable = `${entry.group} ${entry.subgroup} ${entry.label}`.toLocaleLowerCase();
        return entry.numbers.includes(target.digits) ||
          target.terms.some((term) => searchable.includes(term));
      })
      .sort((left, right) => {
        const score = (entry: KnownNumberEntry) => {
          const searchable = `${entry.group} ${entry.subgroup} ${entry.label}`.toLocaleLowerCase();
          const termIndex = target.terms.findIndex((term) => searchable.includes(term));
          return (entry.numbers.includes(target.digits) ? 0 : 100) +
            (termIndex === -1 ? target.terms.length : termIndex);
        };
        return score(left) - score(right);
      });
    const candidate = candidates[0];
    const part = candidate?.parts.find((item) => item.digits === target.digits) ??
      candidate?.parts[0];
    const projected = candidate && part
      ? { ...candidate, numbers: [part.digits], parts: [part] }
      : undefined;
    if (
      projected &&
      !selected.some((entry) => entry.id === projected.id) &&
      flattenKnownNumberEntries([...selected, projected]).length <= maximumRows
    ) {
      selected.push(projected);
    }
  }
  return selected;
}

export function knownNumberAtPointer(
  value: string,
  entries: KnownNumberEntry[],
  metrics: TextareaPointerMetrics,
): KnownNumberPointerMatch | null {
  const lineIndex = numberLineIndexAtPointer(value, metrics);
  if (lineIndex === null) {
    return null;
  }
  const number = value.split(/\r?\n/)[lineIndex]?.trim();
  for (const entry of entries) {
    const part = entry.parts.find((candidate) => candidate.digits === number);
    if (part) {
      return { entry, part };
    }
  }
  return null;
}

export function knownNumberPartAtPointer(
  value: string,
  parts: KnownNumberPart[],
  metrics: TextareaPointerMetrics,
): KnownNumberPart | null {
  const lineIndex = numberLineIndexAtPointer(value, metrics);
  if (lineIndex === null) {
    return null;
  }
  const number = value.split(/\r?\n/)[lineIndex]?.trim();
  return parts.find((part) => part.digits === number) ?? null;
}

export function numberLineIndexAtPointer(
  value: string,
  metrics: TextareaPointerMetrics,
): number | null {
  if (
    metrics.lineHeight <= 0 ||
    metrics.pointerY < metrics.paddingTop
  ) {
    return null;
  }
  const lineIndex = Math.floor(
    (metrics.pointerY - metrics.paddingTop + metrics.scrollTop) /
      metrics.lineHeight,
  );
  const number = value.split(/\r?\n/)[lineIndex]?.trim();
  return number ? lineIndex : null;
}
